import './style.css';
import examples from 'virtual:examples';
import { DODGE_DEFAULTS, type DodgeRequest, Dodger, type Stuck } from './dodge.ts';
import { blockAt, createEditor, flash, replaceKeepingCursor, setText, showErrors } from './editor.ts';
import { Heat } from './heat.ts';
import { Collider, PLAYER_HIT } from './hits.ts';
import { enemyGroups, hitRadii, isEnemy, kindSpec, kinds } from './kinds.ts';
import { KindsPanel } from './kinds-panel.ts';
import { Log } from './log.ts';
import { type Mode, Mover, modes, type Point } from './movement.ts';
import { HIT_FLASH, Renderer } from './renderer.ts';
import { type LocalFile, Serve, serveBase } from './serve.ts';
import { isShareHash, LONG_LINK, makeShare, readShare, SHARE_DEFAULTS, type Share, shareLink } from './share.ts';
import {
  BOSS,
  type EvalResult,
  FIELD,
  type Inputs,
  PLAYER_START,
  Sim,
  type SourceError,
  setFieldWidth,
  type VmEvent,
} from './sim.ts';
import { format } from './vm.ts';

const TICK = 1 / 60;
const MAX_TICKS_PER_FRAME = 8;
const EDIT_KEY = 'bs.edit.';
const MIN_FIELD_WIDTH = 256;

function $<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el as T;
}

const ui = {
  field: $<HTMLCanvasElement>('field'),
  code: $<HTMLDivElement>('editor'),
  example: $<HTMLSelectElement>('example'),
  enemy: $<HTMLSelectElement>('enemy'),
  move: $<HTMLSelectElement>('move'),
  play: $<HTMLButtonElement>('play'),
  step: $<HTMLButtonElement>('step'),
  speed: $<HTMLSelectElement>('speed'),
  reset: $<HTMLButtonElement>('reset'),
  cancel: $<HTMLButtonElement>('cancel'),
  hitboxes: $<HTMLButtonElement>('hitboxes'),
  heatToggle: $<HTMLButtonElement>('heat-toggle'),
  heat: $<HTMLCanvasElement>('heat'),
  dodge: $<HTMLButtonElement>('dodge'),
  dodgeBeam: $<HTMLInputElement>('dodge-beam'),
  dodgeHorizon: $<HTMLInputElement>('dodge-horizon'),
  dodgeTicks: $<HTMLInputElement>('dodge-ticks'),
  difficulty: $<HTMLSelectElement>('difficulty'),
  beatOn: $<HTMLInputElement>('beat-on'),
  bpm: $<HTMLInputElement>('bpm'),
  beatOffset: $<HTMLInputElement>('beat-offset'),
  metronome: $<HTMLInputElement>('metronome'),
  beatDot: $<HTMLElement>('beat-dot'),
  beatStat: $<HTMLElement>('s-beat'),
  beatStatDot: $<HTMLElement>('s-beat-dot'),
  beatStatN: $<HTMLElement>('s-beat-n'),
  revert: $<HTMLButtonElement>('revert'),
  share: $<HTMLButtonElement>('share'),
  download: $<HTMLButtonElement>('download'),
  format: $<HTMLButtonElement>('format'),
  fullscreen: $<HTMLButtonElement>('fullscreen'),
  helpToggle: $<HTMLButtonElement>('help-toggle'),
  codeToggle: $<HTMLButtonElement>('code-toggle'),
  settingsToggle: $<HTMLButtonElement>('settings-toggle'),
  settings: $<HTMLElement>('settings'),
  varRows: $<HTMLElement>('var-rows'),
  help: $<HTMLElement>('help'),
  tabKeys: $<HTMLButtonElement>('tab-keys'),
  tabKinds: $<HTMLButtonElement>('tab-kinds'),
  tabEnv: $<HTMLButtonElement>('tab-env'),
  helpKeys: $<HTMLElement>('help-keys'),
  helpKinds: $<HTMLElement>('help-kinds'),
  helpEnv: $<HTMLElement>('help-env'),
  rank: $<HTMLInputElement>('rank'),
  rankOut: $<HTMLOutputElement>('rank-out'),
  seed: $<HTMLInputElement>('seed'),
  scrub: $<HTMLInputElement>('scrub'),
  scrubOut: $<HTMLOutputElement>('scrub-out'),
  log: $<HTMLDivElement>('log'),
  paused: $<HTMLSpanElement>('s-paused'),
  tick: $<HTMLElement>('s-tick'),
  actors: $<HTMLElement>('s-actors'),
  lasers: $<HTMLElement>('s-lasers'),
  hits: $<HTMLElement>('s-hits'),
  peakStat: $<HTMLElement>('s-peak-stat'),
  peak: $<HTMLElement>('s-peak'),
  ms: $<HTMLElement>('s-ms'),
  fps: $<HTMLElement>('s-fps'),
  serve: $<HTMLElement>('s-serve'),
  serveHost: $<HTMLElement>('s-serve-host'),
};

const log = new Log(ui.log);
resizeField();
const sim = await Sim.create(readSeed());
const renderer = createRenderer();
const spritesLoading = renderer.loadSprites();
const heat = new Heat(ui.heat);
const collider = new Collider();
/** Hit radius by kind id. Ids belong to one VM, so a reset clears it. */
let hitByKind: number[] = [];
/** Ticks left of the ship's hit flash. */
let hitFlash = 0;
let showHitboxes = false;
let showHeat = false;
const dodger = new Dodger();
/**
 * The dodge bot's run while it plays back: where it stood after each tick of its run, which
 * started at tick `base` on the VM's clock, and where it got stuck.
 */
let bot: { base: number; xs: Float64Array; ys: Float64Array; stuck: Stuck[]; ended: boolean } | null = null;

let saveTimer = 0;
const editor = createEditor(ui.code, {
  runAll: () => runAll(),
  runBlock: () => runBlock(),
  reset: () => reset(),
  pause: () => setPlaying(!playing),
  step: () => stepOnce(),
  back: () => stepBack(),
  share: (ascii) => void share(ascii),
  format: () => formatCode(),
  onChange: () => {
    clearTimeout(saveTimer);
    saveTimer = window.setTimeout(saveEdit, 400);
  },
});

const kindsPanel = new KindsPanel($('kind-list'), renderer, editor);

// Warn once per unknown kind name; a hard reset starts the list over.
const unknownKinds = new Set<string>();
renderer.onUnknownKind = (name) => {
  if (unknownKinds.has(name)) return;
  unknownKinds.add(name);
  log.print('warning', `unknown kind '${name}, drawn as bullet`);
};

// var1 to var8: named inputs the settings menu sets. Unset ones are absent, so scripts'
// defaults apply. Changes queue up and go into the next tick's inputs, which keeps rewind exact.
const VAR_NAMES = Array.from({ length: 8 }, (_, i) => `var${i + 1}`);
const vars = new Map<string, number>();
/** Named inputs (rank, difficulty and the vars) changed since the last tick. */
let inputChanges: [string, number | null][] = [];

const DIFFICULTIES = ['easy', 'normal', 'hard', 'lunatic'];
/** 0 to 3, or null for unset, which scripts treat as normal. */
let difficulty: number | null = null;
/** The beat clock as [bpm, offset in ticks], or null when it's off. */
let beat: [number, number] | null = null;
/** A beat clock change waiting for the next tick; undefined means none. */
let beatChange: [number, number] | null | undefined;
/** A cancel waiting for the next tick, with the tags to cancel ([] for every one). */
let cancelChange: string[] | undefined;
const varRows = new Map<string, { row: HTMLElement; slider: HTMLInputElement; field: HTMLInputElement }>();

/** Something the examples dropdown can open. */
interface Entry {
  slug: string;
  title: string;
  description: string;
  /** File name for downloads, without .bs. */
  name: string;
  /** null until fetched: BulletML conversions load on demand. */
  source: string | null;
  path?: string;
  /** Whether edits are kept in the browser. Shared and dropped scripts don't touch saved edits. */
  keepEdits: boolean;
  /** For a shared script, the hash that opens it again, without the #. */
  fragment?: string;
}

const SHARED = ':shared';
const DROPPED = ':dropped';
const entries = new Map<string, Entry>();
for (const ex of examples) {
  entries.set(ex.slug, {
    slug: ex.slug,
    title: ex.title,
    description: ex.description,
    name: ex.slug,
    source: ex.source,
    keepEdits: true,
  });
}

let current: Entry | undefined = entries.values().next().value;
/** The actor running `main`, which the host moves around. */
let boss: number | null = null;
/** Where the page last put the enemy. Static mode only moves it again when that changes, so a
 * script's own move-to isn't undone every tick. */
let placed: { id: number; x: number; y: number } | null = null;
let enemyKind = 'boss';
const mover = new Mover();
let enemyPos: Point = { ...BOSS };
let playing = true;
let speed = 1;
let acc = 0;
let lastFrame = performance.now();
let fps = 60;
let vmMs = 0;

const pointer = { ...PLAYER_START };
const player = { ...PLAYER_START, vx: 0 };
let lastTickPos = { ...PLAYER_START };

sim.onEvents = handleEvents;

// For poking at the VM from the browser console.
Object.assign(globalThis, {
  bs: {
    sim,
    renderer,
    editor,
    heat,
    collider,
    get lastShare() {
      return lastShare;
    },
  },
});

function createRenderer(): Renderer {
  try {
    return new Renderer(ui.field);
  } catch (err) {
    log.print('error', err instanceof Error ? err.message : String(err));
    throw err;
  }
}

function readSeed(): number {
  const n = Math.floor(Number(ui.seed.value));
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), 0xffffffff) : 1;
}

// Pickers and the URL

const exampleGroups = new Map<string, HTMLOptGroupElement>();
for (const ex of examples) {
  if (ex.group === null) {
    ui.example.append(new Option(ex.title, ex.slug));
    continue;
  }
  let group = exampleGroups.get(ex.group);
  if (!group) {
    group = document.createElement('optgroup');
    group.label = ex.group;
    exampleGroups.set(ex.group, group);
    ui.example.append(group);
  }
  group.append(new Option(ex.title, ex.slug));
}
for (const group of enemyGroups) {
  const el = document.createElement('optgroup');
  el.label = group.label;
  for (const o of group.options) el.append(new Option(o.label, o.kind));
  ui.enemy.append(el);
}

/**
 * Adds the BulletML conversions from public/bulletml/index.json, if `npm run bulletml` has made
 * them, as one group each below the bs examples. Their scripts load when picked.
 */
async function loadBulletmlIndex(): Promise<void> {
  const indexUrl = new URL('bulletml/index.json', location.href);
  let list: { group: string; name: string; title: string; path: string }[];
  try {
    const res = await fetch(indexUrl);
    if (!res.ok) return;
    list = await res.json();
  } catch {
    return;
  }
  const groups = new Map<string, HTMLOptGroupElement>();
  for (const item of list) {
    // The slug follows the file layout (bulletml/daiouzyou/round-1-boss.bs), so URLs stay lowercase.
    const slug = item.path.replace(/^\/?bulletml\//, '').replace(/\.bs$/, '');
    // Paths may be relative to the site root (bulletml/...) or to the index itself.
    const base = item.path.startsWith('/') || item.path.startsWith('bulletml/') ? `${location.origin}/` : indexUrl;
    entries.set(slug, {
      slug,
      title: item.title || item.name,
      description: `BulletML: ${item.group}/${item.name}`,
      name: item.name,
      source: null,
      path: new URL(item.path, base).href,
      keepEdits: true,
    });
    let group = groups.get(item.group);
    if (!group) {
      group = document.createElement('optgroup');
      group.label = item.group;
      groups.set(item.group, group);
      ui.example.append(group);
    }
    group.append(new Option(item.title || item.name, slug));
  }
}

/** Puts a shared or dropped script at the top of the dropdown, replacing the last one. */
function addOpened(entry: Entry): void {
  entries.set(entry.slug, entry);
  const option = [...ui.example.options].find((o) => o.value === entry.slug) ?? new Option();
  option.value = entry.slug;
  option.textContent = entry.title;
  if (!option.parentElement) ui.example.prepend(option);
}

// Local files from bs serve

const LOCAL = 'local/';
let served: Serve | null = null;
let localGroup: HTMLOptGroupElement | null = null;

function localSlug(path: string): string {
  return LOCAL + path.replace(/\.bs$/, '');
}

/**
 * Connects to bs serve and lists its files at the top of the dropdown. Returns the first file's
 * slug, to open when the address names no example.
 */
async function startServe(base: string): Promise<string | null> {
  served = new Serve(base, { files: showLocalFiles, changed: (path) => void localChanged(path), status: showServe });
  showServe(false);
  served.connect();

  // A page on bs.ell.dev may have to wait while the browser asks to let it reach local apps, so
  // the start doesn't wait long: the first file opens whenever the list arrives.
  const listing = served.list();
  const late = new Promise<null>((resolve) => setTimeout(() => resolve(null), 1500));

  try {
    const list = await Promise.race([listing, late]);
    if (list !== null) return list.length > 0 ? localSlug(list[0].path) : null;

    log.print(
      'note',
      `waiting for bs serve at ${base}; if the browser asks to let this page reach apps on your device, allow it`,
    );
    void listing
      .then((files) => {
        if (files.length > 0 && !current?.slug.startsWith(LOCAL)) void selectExample(localSlug(files[0].path));
      })
      .catch(() => {});
    return null;
  } catch (err) {
    log.print('error', `couldn't reach bs serve at ${base}: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}

/** Replaces the "Local files" group with `list`, keeping scripts already fetched. */
function showLocalFiles(list: LocalFile[]): void {
  if (!served) return;
  const listed = new Set(list.map((file) => localSlug(file.path)));
  for (const slug of [...entries.keys()]) {
    if (slug.startsWith(LOCAL) && !listed.has(slug)) entries.delete(slug);
  }

  for (const file of list) {
    const slug = localSlug(file.path);

    // Keep the same object for files already listed: `current` may be one of them.
    const known = entries.get(slug);
    if (known) {
      known.title = file.title;
      continue;
    }

    entries.set(slug, {
      slug,
      title: file.title,
      description: `bs serve: ${file.path}`,
      name: file.path.replace(/\.bs$/, '').split('/').pop() ?? slug,
      source: null,
      path: served.fileUrl(file.path),
      // The file on disk is the copy that counts, so edits here aren't kept.
      keepEdits: false,
    });
  }

  if (!localGroup) {
    localGroup = document.createElement('optgroup');
    localGroup.label = 'Local files';
    ui.example.prepend(localGroup);
  }
  localGroup.replaceChildren(...list.map((file) => new Option(file.title, localSlug(file.path))));
  if (current) {
    ui.example.value = current.slug;
    document.title = `${current.title} | bs testbed`;
  }
}

/** A file changed on disk: reload it if it's open, as Ctrl+Enter would, or refetch it later. */
async function localChanged(path: string): Promise<void> {
  if (!served) return;
  const slug = localSlug(path);
  const entry = entries.get(slug);
  if (!entry || current !== entry) {
    if (entry) entry.source = null;
    await served.list().catch(() => undefined);
    return;
  }

  let text: string;
  try {
    text = await served.source(path);
  } catch {
    log.print('note', `${path} is gone from disk`);
    await served.list().catch(() => undefined);
    return;
  }

  if (text === entry.source) return;
  if (entry.source !== null && editor.state.doc.toString() !== entry.source) {
    log.print('note', `${path} changed on disk, which replaces your edits here`);
  }
  entry.source = text;
  setText(editor, text);
  ui.revert.hidden = true;
  runAll();

  // Its title may have changed too.
  await served.list().catch(() => undefined);
}

function showServe(online: boolean): void {
  if (!served) return;
  ui.serve.hidden = false;
  ui.serve.classList.toggle('offline', !online);
  ui.serveHost.textContent = online ? served.label : `${served.label} (offline)`;
}

/** Startup picks the enemy and movement before the example, so the URL waits until it's done. */
let urlReady = false;

/** Keeps ?example=, &enemy= and &move= in step with the pickers, Hydra style. */
function writeUrl(): void {
  if (!urlReady) return;

  const url = new URL(location.href);
  url.hash = '';
  if (current?.fragment) {
    url.searchParams.delete('example');
    url.hash = current.fragment;
  } else if (current && current.slug !== DROPPED) {
    url.searchParams.set('example', current.slug);
  } else {
    url.searchParams.delete('example');
  }
  url.searchParams.set('enemy', enemyKind);
  url.searchParams.set('move', mover.mode);
  for (const [name, on] of [
    ['hitboxes', showHitboxes],
    ['heat', showHeat],
  ] as const) {
    if (on) url.searchParams.set(name, '1');
    else url.searchParams.delete(name);
  }
  if (difficulty === null) url.searchParams.delete('difficulty');
  else url.searchParams.set('difficulty', DIFFICULTIES[difficulty]);
  for (const name of VAR_NAMES) {
    const value = vars.get(name);
    if (value === undefined) url.searchParams.delete(name);
    else url.searchParams.set(name, String(value));
  }
  // Keep group/name and host:port readable; a slash or colon is fine in a query.
  url.search = url.searchParams.toString().replace(/%2F/gi, '/').replace(/%3A/gi, ':');
  history.replaceState(null, '', url);
}

function editedSource(slug: string): string | null {
  try {
    return localStorage.getItem(EDIT_KEY + slug);
  } catch {
    return null;
  }
}

function saveEdit(): void {
  if (!current || current.source === null) return;
  const text = editor.state.doc.toString();
  ui.revert.hidden = text === current.source;
  if (!current.keepEdits) return;
  try {
    if (text === current.source) localStorage.removeItem(EDIT_KEY + current.slug);
    else localStorage.setItem(EDIT_KEY + current.slug, text);
  } catch {
    // Edits just won't survive a reload.
  }
}

/** Opens a dropdown entry by slug, fetching its script first if it isn't loaded yet. */
async function selectExample(slug: string | null): Promise<void> {
  const wanted = slug !== null && (entries.get(slug) ?? entries.get(slug.toLowerCase()));
  const entry = wanted || entries.get(examples[0]?.slug ?? '');
  if (!entry) return;
  if (entry.source === null) {
    try {
      const res = await fetch(entry.path ?? '');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      entry.source = await res.text();
    } catch (err) {
      log.print('error', `couldn't load ${entry.slug}: ${err instanceof Error ? err.message : String(err)}`);
      if (current) ui.example.value = current.slug;
      return;
    }
  }
  openEntry(entry);
}

function openEntry(entry: Entry): void {
  if (entry.source === null) return;
  current = entry;
  ui.example.value = entry.slug;
  ui.example.title = entry.description || entry.title;
  document.title = `${entry.title} | bs testbed`;
  const edited = entry.keepEdits ? editedSource(entry.slug) : null;
  setText(editor, edited ?? entry.source);
  ui.revert.hidden = edited === null;
  writeUrl();
  reset();
}

/** The first comment line of a script, if it has one, for naming shared and dropped scripts. */
function titleOf(src: string): string | null {
  const m = /^\s*;+\s*(.+)/.exec(src);
  return m ? m[1].trim() : null;
}

// Sharing: links that carry the script and scene, .bs downloads, and dropped files

/** Sets enemy, movement, rank, seed and vars from a share; missing fields mean the defaults. */
function applyShareScene(s: Share): void {
  selectEnemy(s.enemy ?? SHARE_DEFAULTS.enemy);
  selectMove(s.move ?? SHARE_DEFAULTS.move);
  setRank(s.rank ?? SHARE_DEFAULTS.rank);
  ui.seed.value = String(s.seed ?? SHARE_DEFAULTS.seed);
  for (const name of VAR_NAMES) setVar(name, s.vars?.[name] ?? null);
  setDifficulty(s.difficulty ?? null);
  setBeatClock(s.beat ?? null);
}

function openShared(s: Share, fragment: string): void {
  const title = titleOf(s.src);
  addOpened({
    slug: SHARED,
    title: `Shared: ${title ?? 'script'}`,
    description: 'Opened from a share link. Your saved edits to the examples are untouched.',
    name: 'shared',
    source: s.src,
    keepEdits: false,
    fragment,
  });
  if (s.sim !== sim.simVersion()) {
    log.print(
      'warning',
      `this share is from simulation version ${s.sim} and this VM is ${sim.simVersion()}, so it may behave differently`,
    );
  }
  openEntry(entries.get(SHARED) as Entry);
}

/**
 * Ctrl+S or the share button: copy a link that opens this script in this scene. The short #z=
 * form by default; Shift (or Ctrl+Shift+S) copies the plain ASCII #s= form.
 */
async function share(ascii: boolean): Promise<void> {
  const form = ascii ? 's' : 'z';
  const link = await shareLink(
    makeShare({
      sim: sim.simVersion(),
      src: editor.state.doc.toString(),
      enemy: enemyKind,
      move: mover.mode,
      rank: Number(ui.rank.value),
      seed: readSeed(),
      vars,
      beat,
      difficulty,
    }),
    form,
  );
  lastShare = link;
  const copied = await copyText(link);
  const kind = ascii ? 'an ASCII #s= share link' : 'a #z= share link';
  const what = copied ? `copied ${kind}` : `couldn't reach the clipboard, so ${kind} is in the address bar`;
  if (!copied) history.replaceState(null, '', link);
  if (link.length > LONG_LINK) {
    log.action(
      'warning',
      `${what}, ${link.length} characters. Chat apps may cut links this long.`,
      'download .bs',
      download,
    );
  } else {
    log.print('value', `${what}, ${link.length} characters`);
  }
}

let lastShare = '';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older browsers, or no clipboard permission: fall back to a selected textarea.
    const area = Object.assign(document.createElement('textarea'), { value: text });
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.append(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    editor.focus();
    return ok;
  }
}

/** Lays the code out the way the examples are written, at their width. */
function formatCode(): void {
  const result = format(editor.state.doc.toString(), 60);

  if (!result.ok) {
    for (const e of result.errors) log.error(e, "can't format: ");
    return;
  }

  if (!replaceKeepingCursor(editor, result.text)) log.print('note', 'already formatted');
  editor.focus();
}

/** Saves the editor as <example-name>.bs. */
function download(): void {
  const blob = new Blob([editor.state.doc.toString()], { type: 'text/plain' });
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(blob),
    download: `${current?.name ?? 'pattern'}.bs`,
  });
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** Opens a .bs file dropped on the page. BulletML .xml joins once the converter exists. */
async function openDropped(file: File): Promise<void> {
  if (!file.name.toLowerCase().endsWith('.bs')) {
    log.print('error', `can't open ${file.name}: only .bs files can be dropped for now`);
    return;
  }
  const src = await file.text();
  const name = file.name.replace(/\.bs$/i, '');
  addOpened({
    slug: DROPPED,
    title: `Dropped: ${file.name}`,
    description: `${file.name}, dropped onto the page`,
    name,
    source: src,
    keepEdits: false,
  });
  openEntry(entries.get(DROPPED) as Entry);
}

function selectEnemy(kind: string): void {
  enemyKind = kinds[kind] ? kind : 'boss';
  ui.enemy.value = enemyKind;
  writeUrl();
}

function selectMove(mode: string): void {
  const m = (modes as readonly string[]).includes(mode) ? (mode as Mode) : 'static';
  mover.setMode(m, sim.tick, enemyPos);
  ui.move.value = m;
  writeUrl();
}

// Evaluating code

/**
 * Ctrl+Enter. Loads the whole file into the running VM and restarts main where the enemy is.
 * Bullets already in flight keep going and pick up redefinitions on their next call. A failed
 * load changes nothing.
 */
function runAll(): void {
  bot = null;
  const doc = editor.state.doc;
  flash(editor, 0, doc.length);
  const result = sim.load(doc.toString());
  const warnings = result.ok ? result.warnings : [];
  if (!result.ok) {
    showErrors(editor, result.errors, warnings);
    for (const e of result.errors) log.error(e);
    for (const w of warnings) log.warn(w);
    return;
  }
  showErrors(editor, [], warnings);
  log.clearCodeErrors();
  for (const w of warnings) log.warn(w);
  log.print('defined', `loaded ${result.names.join(', ') || 'nothing'}`);
  respawn();
}

/** Replaces the enemy running main with a fresh one, at the same spot. */
function respawn(): void {
  if (boss !== null) sim.kill(boss);
  boss = null;
  if (!sim.names().some((n) => n.name === 'main')) {
    log.print('note', 'no main to start; Alt+Enter on a form runs it from the enemy');
    return;
  }
  const spawned = sim.spawn('main', enemyPos.x, enemyPos.y, enemyKind);
  if (!spawned.ok) {
    for (const e of spawned.errors) log.error(e);
    return;
  }
  boss = spawned.id;
  placed = { id: boss, x: enemyPos.x, y: enemyPos.y };
}

/** Alt+Enter. Evaluates the form under the cursor, or the selection, from the enemy. */
function runBlock(): void {
  bot = null;
  const block = blockAt(editor.state);
  if (!block) {
    log.print('note', 'put the cursor on a form to evaluate it');
    return;
  }
  flash(editor, block.from, block.to);
  const result = sim.eval(block.text, enemyPos.x, enemyPos.y);
  // Positions come back relative to the block; point them at the document instead.
  const shift = (e: SourceError): SourceError =>
    e.line < 1 ? e : { ...e, line: e.line + block.line - 1, col: e.line === 1 ? e.col + block.col - 1 : e.col };
  const warnings = (result.ok ? result.warnings : []).map(shift);
  for (const w of warnings) log.warn(w);
  if (!result.ok) {
    const errors = result.errors.map(shift);
    showErrors(editor, errors, warnings);
    for (const e of errors) log.error(e);
    return;
  }
  // A runtime error still leaves the definitions in place, so they're reported too.
  const failed = result.error === undefined ? [] : [shift(result.error)];
  showErrors(editor, failed, warnings);
  printResult(result);
  for (const e of failed) log.error(e);
}

function printResult(r: EvalResult): void {
  if (!r.ok) return;
  if (r.defined && r.defined.length > 0) log.print('defined', `defined ${r.defined.join(', ')}`);
  if (r.value !== undefined) log.print('value', r.value);
  if (r.running !== undefined) log.print('running', `running as actor ${r.running}`);
}

/** Ctrl+Shift+Enter. A fresh VM with the seed, the file reloaded and main respawned at home. */
function reset(): void {
  sim.reset(readSeed());
  renderer.resetKinds();
  unknownKinds.clear();
  sim.setVarNow('rank', Number(ui.rank.value));
  sim.setVarNow('difficulty', difficulty);
  if (beat) sim.setBeatNow(beat);
  for (const [name, value] of vars) sim.setVarNow(name, value);
  inputChanges = [];
  beatChange = undefined;
  cancelChange = undefined;
  boss = null;
  enemyPos = { ...BOSS };
  mover.restart(sim.tick, enemyPos);
  hitByKind = [];
  hitFlash = 0;
  collider.reset();
  heat.clear();
  setPlaying(true);
  runAll();
}

function handleEvents(events: VmEvent[], dropped: number): void {
  if (dropped > 0) log.print('warning', `${dropped} events were dropped; the VM keeps the newest`);
  for (const e of events) {
    switch (e.type) {
      case 'emit': {
        const value = e.value === undefined ? '' : ` ${fmt(e.value)}`;
        log.print('event', `emit '${e.name}${value} from actor ${e.id} at (${fmt(e.x)}, ${fmt(e.y)})`);
        break;
      }
      case 'cancelled':
        if (e.x !== null && e.y !== null) renderer.item(e.x, e.y, e.kind);
        break;
      case 'error':
        log.error(e, `actor ${e.id} stopped: `);
        break;
      case 'despawned': {
        if (e.id === boss && e.reason !== 'kill') boss = null;
        // Only a script's own (vanish) gets an effect: not culls, and not an enemy being replaced.
        // Enemies explode, and actors that aren't drawn, like emitters, leave nothing.
        if (e.reason !== 'vanish' || e.x === null || e.y === null || kinds[e.kind]?.hidden) break;
        renderer.burst(e.x, e.y, e.id === boss || isEnemy(e.kind));
        break;
      }
    }
  }
}

/** Events carry null for numbers that aren't finite. */
function fmt(n: number | null): string {
  if (n === null) return '?';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// Simulation

function liveTick(): void {
  // While the bot's run plays back, the bot flies the ship, so bullets aim where they did for
  // it, and the enemy stays where it spawned, as it did in the bot's VM.
  const run = bot;
  const k = botIndex(sim.tick);
  const flying = run !== null && k >= 0 && k + 1 < run.xs.length;
  if (run && !run.ended && k === run.xs.length - 1) {
    run.ended = true;
    log.print('note', "that's the end of the bot's run; the ship follows the pointer again");
  }

  let enemy: Inputs['enemy'] = null;
  if (!flying) {
    enemyPos = mover.position(sim.tick, sim.seed, enemyPos, pointer);
    const still = mover.mode === 'static' && placed?.id === boss && placed.x === enemyPos.x && placed.y === enemyPos.y;
    if (boss !== null && !still) placed = { id: boss, x: enemyPos.x, y: enemyPos.y };
    if (boss !== null && !still) enemy = { id: boss, x: enemyPos.x, y: enemyPos.y };
  }

  const tx = flying ? run.xs[k] : pointer.x;
  const ty = flying ? run.ys[k] : pointer.y;
  // The bot aims each tick with its last move as the velocity, starting from rest.
  const from = flying ? { x: k > 0 ? run.xs[k - 1] : tx, y: k > 0 ? run.ys[k - 1] : ty } : lastTickPos;
  const inputs: Inputs = {
    tx,
    ty,
    vx: tx - from.x,
    vy: ty - from.y,
    w: FIELD.w,
    enemy,
    named: inputChanges,
    beat: beatChange,
    cancel: cancelChange,
  };
  inputChanges = [];
  beatChange = undefined;
  cancelChange = undefined;
  lastTickPos = { x: tx, y: ty };
  player.vx = inputs.vx;
  sim.step(inputs);
  vmMs = vmMs * 0.9 + sim.tickMs * 0.1;

  // Only live ticks count: a rewind replays history without hitting anything again. The bot is
  // hit where it moves to this tick, as it was in its search.
  const hx = flying ? run.xs[k + 1] : tx;
  const hy = flying ? run.ys[k + 1] : ty;
  const view = sim.bullets();
  if (collider.check(view, sim.lasers(), hx, hy, hitRadius) > 0) {
    hitFlash = HIT_FLASH;
    renderer.burst(hx, hy, false);
  } else if (hitFlash > 0) {
    hitFlash--;
  }
  heat.add(view);
}

/** Where tick `tick` falls in the bot's run, or -1 outside it. */
function botIndex(tick: number): number {
  if (!bot) return -1;
  const k = tick - bot.base;
  return k >= 0 && k < bot.xs.length ? k : -1;
}

function hitRadius(kind: number): number {
  let r = hitByKind[kind];
  if (r === undefined) r = hitByKind[kind] = kindSpec(sim.kindName(kind)).hit;
  return r;
}

/** A trap inside the VM leaves it unusable. Stop, say so, and let reset build a new one. */
function crashed(err: unknown): void {
  setPlaying(false);
  const message = err instanceof Error ? err.message : String(err);
  log.print('error', `the VM crashed: ${message}. Ctrl+Shift+Enter starts a fresh one.`);
  console.error(err);
}

function setPlaying(on: boolean): void {
  playing = on;
  ui.play.textContent = on ? 'pause' : 'play';
  ui.play.setAttribute('aria-pressed', String(!on));
  ui.paused.hidden = on;
  acc = 0;
}

function stepOnce(): void {
  setPlaying(false);
  try {
    liveTick();
  } catch (err) {
    crashed(err);
  }
}

function stepBack(): void {
  setPlaying(false);
  seek(sim.tick - 1);
}

/** Rewinds or fast-forwards inside the history, and picks the enemy up where it was then. */
function seek(t: number): void {
  sim.seek(t);
  const last = sim.recorded(sim.tick - 1);
  if (last?.enemy) enemyPos = { x: last.enemy.x, y: last.enemy.y };
}

function frame(now: number): void {
  const dt = Math.min(0.25, (now - lastFrame) / 1000);
  lastFrame = now;
  if (dt > 0) fps = fps * 0.95 + (1 / dt) * 0.05;

  if (playing) {
    acc += dt * speed;
    let n = 0;
    try {
      while (acc >= TICK && n < MAX_TICKS_PER_FRAME) {
        liveTick();
        acc -= TICK;
        n++;
      }
    } catch (err) {
      crashed(err);
    }
    if (n === MAX_TICKS_PER_FRAME) acc = 0;
  }

  // While rewound, draw the ship where it was then; while the bot plays, where the bot is.
  const at = botIndex(sim.tick);
  const then = sim.tick < sim.newestTick ? sim.recorded(sim.tick - 1) : undefined;
  if (bot && at >= 0) {
    player.x = bot.xs[at];
    player.y = bot.ys[at];
  } else {
    player.x = then ? then.tx : pointer.x;
    player.y = then ? then.ty : pointer.y;
  }
  if (!playing) player.vx = 0;

  const view = sim.bullets();
  const lasers = sim.lasers();
  renderer.draw(view, lasers, (k) => sim.kindName(k), player, {
    hitboxes: showHitboxes,
    hitRadius,
    flash: hitFlash,
    bot: bot && at >= 0 ? { xs: bot.xs, ys: bot.ys, at, stuck: bot.stuck } : null,
  });
  if (showHeat) heat.draw();
  updateStats(view.count, lasers.count);
  updateTimeline();
  updateBeat();
  requestAnimationFrame(frame);
}

/**
 * The field is always 448 units tall and as wide as the window's shape allows, so it fills the
 * window with no bars. Live bullets stay put; the enemy's home and the player's bounds follow.
 */
function resizeField(): void {
  const w = Math.max(MIN_FIELD_WIDTH, Math.round((FIELD.h * window.innerWidth) / window.innerHeight));
  setFieldWidth(w);
}

function layout(): void {
  resizeField();
  renderer.fit(window.innerWidth, window.innerHeight);
  heat.fit(FIELD.w, FIELD.h, ui.field.getBoundingClientRect());
  pointer.x = Math.min(pointer.x, FIELD.w - 8);
  lastTickPos.x = Math.min(lastTickPos.x, FIELD.w - 8);
}

let statsFrame = 0;
function updateStats(actors: number, lasers: number): void {
  if (statsFrame++ % 6 !== 0) return;
  ui.tick.textContent = String(sim.tick);
  ui.actors.textContent = String(actors);
  ui.lasers.textContent = String(lasers);
  ui.hits.textContent = String(collider.hits);
  ui.peak.textContent = heat.peak.toFixed(1);
  ui.ms.textContent = vmMs.toFixed(2);
  ui.fps.textContent = String(Math.round(fps));
}

function updateTimeline(): void {
  const oldest = sim.oldestTick;
  const newest = sim.newestTick;
  ui.scrub.min = String(oldest);
  ui.scrub.max = String(newest);
  if (document.activeElement !== ui.scrub) ui.scrub.value = String(sim.tick);
  ui.scrub.disabled = newest - oldest < 2;
  const behind = newest - sim.tick;
  ui.scrubOut.textContent = behind <= 0 ? 'live' : `−${(behind / 60).toFixed(1)}s`;
}

// Input

window.addEventListener('pointermove', (e) => {
  const p = renderer.toWorld(e.clientX, e.clientY);
  pointer.x = Math.min(Math.max(p.x, 8), FIELD.w - 8);
  pointer.y = Math.min(Math.max(p.y, 8), FIELD.h - 8);
});

ui.play.addEventListener('click', () => setPlaying(!playing));
ui.step.addEventListener('click', stepOnce);
ui.reset.addEventListener('click', () => reset());
ui.speed.addEventListener('change', () => {
  speed = Number(ui.speed.value);
});
ui.rank.addEventListener('input', () => {
  setRank(Number(ui.rank.value));
});
ui.seed.addEventListener('change', () => reset());
ui.example.addEventListener('change', () => void selectExample(ui.example.value));
ui.share.addEventListener('click', (e) => void share(e.shiftKey));
ui.cancel.addEventListener('click', () => cancelAll());
ui.hitboxes.addEventListener('click', () => setHitboxes(!showHitboxes));
ui.heatToggle.addEventListener('click', () => setHeat(!showHeat));
ui.dodge.addEventListener('click', () => void dodge());
for (const input of [ui.dodgeBeam, ui.dodgeHorizon, ui.dodgeTicks]) input.addEventListener('change', saveDodgeSettings);
ui.difficulty.addEventListener('change', () => setDifficulty(Number(ui.difficulty.value)));
for (const el of [ui.beatOn, ui.bpm, ui.beatOffset]) el.addEventListener('change', () => beatFromFields());
ui.download.addEventListener('click', () => download());
ui.format.addEventListener('click', () => formatCode());
window.addEventListener('dragover', (e) => {
  if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
});
window.addEventListener('drop', (e) => {
  const file = e.dataTransfer?.files[0];
  if (!file) return;
  e.preventDefault();
  void openDropped(file);
});
ui.enemy.addEventListener('change', () => {
  selectEnemy(ui.enemy.value);
  respawn();
});
ui.move.addEventListener('change', () => selectMove(ui.move.value));
ui.revert.addEventListener('click', () => {
  if (!current || current.source === null) return;
  setText(editor, current.source);
  saveEdit();
  reset();
});
ui.fullscreen.addEventListener('click', () => toggleFullscreen());
ui.helpToggle.addEventListener('click', () => toggleHelp());
ui.settingsToggle.addEventListener('click', () => toggleSettings());
ui.codeToggle.addEventListener('click', () => toggleCode());
ui.scrub.addEventListener('input', () => {
  setPlaying(false);
  seek(Number(ui.scrub.value));
});

/** Queues a named input change for the next tick, replacing any earlier one for that name. */
function queueInput(name: string, value: number | null): void {
  inputChanges = inputChanges.filter(([n]) => n !== name);
  inputChanges.push([name, value]);
}

/** The difficulty input: 0 easy to 3 lunatic, or null for unset, which reads as normal. */
function setDifficulty(value: number | null): void {
  difficulty = value === 1 ? null : value;
  ui.difficulty.value = String(value ?? 1);
  queueInput('difficulty', difficulty);
  writeUrl();
}

/** Turns the beat clock on at [bpm, offset], or off with null. */
function setBeatClock(value: [number, number] | null): void {
  beat = value;
  beatChange = value;
  ui.beatOn.checked = value !== null;
  if (value) {
    ui.bpm.value = String(value[0]);
    ui.beatOffset.value = String(value[1]);
  }
  ui.beatStat.hidden = value === null;
}

/** Reads the beat fields, if the clock is on. */
function beatFromFields(): void {
  const bpm = Number(ui.bpm.value);
  const offset = Number(ui.beatOffset.value);
  if (!ui.beatOn.checked) setBeatClock(null);
  else if (bpm > 0 && Number.isFinite(offset)) setBeatClock([bpm, offset]);
}

/** The beat number at tick t, counting from the offset; -1 before it. */
function beatAt(t: number): number {
  if (!beat) return -1;
  const [bpm, offset] = beat;
  return t < offset ? -1 : Math.floor(((t - offset) * bpm) / 3600);
}

let audio: AudioContext | null = null;

/** A quiet click for the metronome. */
function click(accent: boolean): void {
  audio ??= new AudioContext();
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  const now = audio.currentTime;
  osc.frequency.value = accent ? 1320 : 880;
  gain.gain.setValueAtTime(0.06, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
  osc.connect(gain).connect(audio.destination);
  osc.start(now);
  osc.stop(now + 0.06);
}

let lastBeat = -1;
let beatFlash = 0;

/** Flashes the indicators, and clicks if the metronome is on, as each beat comes round. */
function updateBeat(): void {
  const b = beatAt(sim.tick);
  if (b !== lastBeat && b >= 0) {
    beatFlash = 6;
    ui.beatStatN.textContent = String(b);
    if (ui.metronome.checked && playing) click(b % 4 === 0);
  }
  lastBeat = b;
  const on = beatFlash > 0;
  ui.beatDot.classList.toggle('on', on);
  ui.beatStatDot.classList.toggle('on', on);
  if (beatFlash > 0) beatFlash--;
}

/** The bomb: every cancellable bullet turns into an item on the next tick. */
function cancelAll(): void {
  cancelChange = [];
}

/** The rank slider feeds the sticky `rank` input. */
function setRank(value: number): void {
  ui.rank.value = String(value);
  ui.rankOut.textContent = value.toFixed(2);
  queueInput('rank', value);
}

/** Sets a var (or with null, unsets it) from the settings menu or the URL. */
function setVar(name: string, value: number | null): void {
  if (value === null) vars.delete(name);
  else vars.set(name, value);
  queueInput(name, value);
  const controls = varRows.get(name);
  if (controls) {
    controls.row.classList.toggle('unset', value === null);
    if (value !== null) controls.slider.value = String(value);
    if (document.activeElement !== controls.field) controls.field.value = value === null ? '' : String(value);
  }
  writeUrl();
}

function buildVarRows(): void {
  for (const name of VAR_NAMES) {
    const row = document.createElement('div');
    row.className = 'var-row unset';
    const label = document.createElement('code');
    label.textContent = name;
    const slider = Object.assign(document.createElement('input'), {
      type: 'range',
      min: '0',
      max: '1',
      step: '0.01',
      value: '0',
    });
    slider.setAttribute('aria-label', `${name} slider`);
    const field = Object.assign(document.createElement('input'), { type: 'number', step: 'any', placeholder: 'unset' });
    field.setAttribute('aria-label', `${name} value`);
    const unset = Object.assign(document.createElement('button'), { textContent: '×', title: `Unset ${name}` });
    slider.addEventListener('input', () => setVar(name, Number(slider.value)));
    field.addEventListener('change', () => {
      const value = field.value.trim() === '' ? null : Number(field.value);
      if (value === null || Number.isFinite(value)) setVar(name, value);
    });
    unset.addEventListener('click', () => setVar(name, null));
    row.append(label, slider, field, unset);
    ui.varRows.append(row);
    varRows.set(name, { row, slider, field });
  }
}

function toggleSettings(): void {
  ui.settings.hidden = !ui.settings.hidden;
  ui.settingsToggle.setAttribute('aria-expanded', String(!ui.settings.hidden));
  if (!ui.settings.hidden && !ui.help.hidden) toggleHelp();
}

function toggleHelp(): void {
  ui.help.hidden = !ui.help.hidden;
  ui.helpToggle.setAttribute('aria-expanded', String(!ui.help.hidden));
  if (!ui.help.hidden && !ui.settings.hidden) toggleSettings();
  if (!ui.helpKinds.hidden) kindsPanel.refresh();
}

type Tab = 'keys' | 'kinds' | 'env';

function showTab(tab: Tab): void {
  const tabs: [Tab, HTMLButtonElement, HTMLElement][] = [
    ['keys', ui.tabKeys, ui.helpKeys],
    ['kinds', ui.tabKinds, ui.helpKinds],
    ['env', ui.tabEnv, ui.helpEnv],
  ];
  for (const [name, button, panel] of tabs) {
    panel.hidden = name !== tab;
    button.setAttribute('aria-selected', String(name === tab));
  }
  if (tab === 'kinds') kindsPanel.refresh();
}

ui.tabKeys.addEventListener('click', () => showTab('keys'));
ui.tabKinds.addEventListener('click', () => showTab('kinds'));
ui.tabEnv.addEventListener('click', () => showTab('env'));
$('env-kinds').addEventListener('click', () => showTab('kinds'));
$('env-examples').addEventListener('click', () => {
  toggleHelp();
  ui.example.focus();
  try {
    ui.example.showPicker();
  } catch {
    // Not every browser opens a select from script; focusing it is enough.
  }
});

const CODE_KEY = 'bs.code';

/** Shows or hides the editor. The console and menu stay; the choice survives a reload. */
function showCode(on: boolean): void {
  ui.code.hidden = !on;
  ui.codeToggle.setAttribute('aria-pressed', String(on));
  try {
    localStorage.setItem(CODE_KEY, on ? 'shown' : 'hidden');
  } catch {
    // It just won't be remembered.
  }
  if (on) editor.focus();
}

function toggleCode(): void {
  showCode(Boolean(ui.code.hidden));
}

function savedCodeShown(): boolean {
  try {
    return localStorage.getItem(CODE_KEY) !== 'hidden';
  } catch {
    return true;
  }
}

// The dodge bot

/**
 * Runs the bot on the code in the editor from a fresh start, in a worker. When it's done, the
 * field starts over the same way and plays its run back. Pressing dodge while it runs stops it.
 */
async function dodge(): Promise<void> {
  if (dodger.running) {
    dodger.cancel();
    return;
  }
  const request = dodgeRequest();
  const { beam, horizon, ticks } = request.settings;
  log.print(
    'running',
    `the bot is looking for a way through ${ticks} ticks (beam ${beam}, horizon ${horizon}); stop cancels it`,
  );
  if (mover.mode !== 'static') log.print('note', 'the bot keeps the enemy still, and so will its playback');
  showDodging(0);
  const reply = await dodger.run(request, showDodging);
  showDodging(null);
  if (reply.type === 'stopped') {
    log.print('note', 'stopped the dodge bot');
    return;
  }
  if (reply.type === 'failed') {
    log.print('error', `the dodge bot couldn't run: ${reply.message}`);
    return;
  }

  const took = `${(reply.ms / 1000).toFixed(1)} s`;
  if (reply.stuck.length === 0) log.print('value', `dodged all ${ticks} ticks (${took})`);
  else log.print('warning', `no safe move at ${reply.stuck.map(stretch).join(', ')} (${took})`);
  if (!sameScene(request, dodgeRequest())) {
    log.print('note', "the code or settings changed while the bot ran, so it won't play back");
    return;
  }
  reset();
  bot = {
    base: sim.tick,
    xs: Float64Array.from(reply.path, (p) => p[1]),
    ys: Float64Array.from(reply.path, (p) => p[2]),
    stuck: reply.stuck,
    ended: false,
  };
  log.print('note', 'playing it back: the ghost ship is the bot. Escape stops it.');
}

function stopBot(): void {
  bot = null;
  log.print('note', 'stopped the playback; the ship follows the pointer again');
}

function stretch(s: Stuck): string {
  return s.from === s.to ? `tick ${s.from}` : `ticks ${s.from}–${s.to}`;
}

/** What a reset would set up, for the worker to copy. */
function dodgeRequest(): DodgeRequest {
  const inputs: [string, number][] = [['rank', Number(ui.rank.value)]];
  if (difficulty !== null) inputs.push(['difficulty', difficulty]);
  inputs.push(...vars);
  const radii = hitRadii();
  return {
    source: editor.state.doc.toString(),
    seed: readSeed(),
    field: { w: FIELD.w, h: FIELD.h },
    enemy: { x: BOSS.x, y: BOSS.y, kind: enemyKind },
    start: { x: PLAYER_START.x, y: PLAYER_START.y },
    inputs,
    beat,
    settings: { radius: PLAYER_HIT, speed: DODGE_DEFAULTS.speed, ...dodgeSettings() },
    radii: { names: Object.keys(radii), values: Object.values(radii), fallback: kindSpec('').hit },
  };
}

/** Whether two requests set up the same run, whatever the search settings. */
function sameScene(a: DodgeRequest, b: DodgeRequest): boolean {
  return JSON.stringify({ ...a, settings: null }) === JSON.stringify({ ...b, settings: null });
}

/** While the bot runs, the button shows how far it's got and stops it if pressed. */
function showDodging(percent: number | null): void {
  const on = percent !== null;
  ui.dodge.setAttribute('aria-pressed', String(on));
  ui.dodge.title = on
    ? 'Stop the dodge bot'
    : 'Let a bot find a path through this pattern from a fresh start, then play it back';
  ui.dodge.textContent = on ? `stop ${percent}%` : 'dodge';
}

const DODGE_KEY = 'bs.dodge';

function dodgeSettings(): { beam: number; horizon: number; ticks: number } {
  return {
    beam: whole(ui.dodgeBeam, DODGE_DEFAULTS.beam),
    horizon: whole(ui.dodgeHorizon, DODGE_DEFAULTS.horizon),
    ticks: whole(ui.dodgeTicks, DODGE_DEFAULTS.ticks),
  };
}

/** A number input's whole value within its limits, or `fallback` when it's empty or nonsense. */
function whole(input: HTMLInputElement, fallback: number): number {
  const n = Math.round(Number(input.value));
  if (input.value.trim() === '' || !Number.isFinite(n)) return fallback;
  return Math.min(Math.max(n, Number(input.min)), Number(input.max));
}

function saveDodgeSettings(): void {
  try {
    localStorage.setItem(DODGE_KEY, JSON.stringify(dodgeSettings()));
  } catch {
    // It just won't be remembered.
  }
}

function loadDodgeSettings(): void {
  let saved: Partial<Record<'beam' | 'horizon' | 'ticks', unknown>> = {};
  try {
    saved = JSON.parse(localStorage.getItem(DODGE_KEY) ?? '{}');
  } catch {
    // Start from the defaults.
  }
  const fields = { beam: ui.dodgeBeam, horizon: ui.dodgeHorizon, ticks: ui.dodgeTicks } as const;
  for (const [name, input] of Object.entries(fields) as [keyof typeof fields, HTMLInputElement][]) {
    const value = saved[name];
    input.value = String(typeof value === 'number' ? value : DODGE_DEFAULTS[name]);
  }
}

const HITBOX_KEY = 'bs.hitboxes';
const HEAT_KEY = 'bs.heat';

function setHitboxes(on: boolean): void {
  showHitboxes = on;
  ui.hitboxes.setAttribute('aria-pressed', String(on));
  remember(HITBOX_KEY, on);
  writeUrl();
}

/** The heatmap keeps counting while hidden, so it has the last second ready when it opens. */
function setHeat(on: boolean): void {
  showHeat = on;
  ui.heat.hidden = !on;
  ui.peakStat.hidden = !on;
  ui.heatToggle.setAttribute('aria-pressed', String(on));
  remember(HEAT_KEY, on);
  writeUrl();
}

function remember(key: string, on: boolean): void {
  try {
    localStorage.setItem(key, on ? 'on' : 'off');
  } catch {
    // It just won't be remembered.
  }
}

/** The address wins, so a link opens the way it was sent; then whatever was last chosen here. */
function startToggle(name: string, key: string): boolean {
  const value = params.get(name);
  if (value !== null) return value !== '0';
  try {
    return localStorage.getItem(key) === 'on';
  } catch {
    return false;
  }
}

function toggleFullscreen(): void {
  if (document.fullscreenElement) void document.exitFullscreen();
  else void document.documentElement.requestFullscreen().catch(() => log.print('note', 'fullscreen was refused'));
}

document.addEventListener('fullscreenchange', () => {
  ui.fullscreen.setAttribute('aria-pressed', String(document.fullscreenElement !== null));
});

function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || target.closest('.cm-editor, input, select, textarea') !== null;
}

// The editor handles its own shortcuts; these cover focus being anywhere else.
window.addEventListener('keydown', (e) => {
  if (e.defaultPrevented) return;
  const mod = e.ctrlKey || e.metaKey;
  if (mod && e.shiftKey && e.key.toLowerCase() === 'h') {
    e.preventDefault();
    toggleCode();
    return;
  }
  if (mod && e.key.toLowerCase() === 's') {
    e.preventDefault();
    void share(e.shiftKey);
    return;
  }
  if (mod && e.key === 'Enter') {
    e.preventDefault();
    if (e.shiftKey) reset();
    else runAll();
    return;
  }
  if (e.altKey && !mod) {
    const action = { p: () => setPlaying(!playing), '.': stepOnce, ',': stepBack }[e.key.toLowerCase()];
    if (action) {
      e.preventDefault();
      action();
    }
    return;
  }
  if (typing(e.target) || mod) return;
  if (e.key === ' ') {
    e.preventDefault();
    setPlaying(!playing);
  } else if (e.key === 'f' || e.key === 'F') {
    toggleFullscreen();
  } else if (e.key === 'b' || e.key === 'B') {
    cancelAll();
  } else if (e.key === 'h' || e.key === 'H') {
    setHitboxes(!showHitboxes);
  } else if (e.key === 'd' || e.key === 'D') {
    setHeat(!showHeat);
  } else if (e.key === '.') {
    stepOnce();
  } else if (e.key === ',') {
    stepBack();
  } else if (e.key === 'Escape') {
    if (ui.help.hidden && ui.settings.hidden && bot) stopBot();
    if (!ui.help.hidden) toggleHelp();
    if (!ui.settings.hidden) toggleSettings();
  }
});

window.addEventListener('resize', layout);
const menu = document.querySelector('.menu');
if (menu) {
  new ResizeObserver(() => {
    document.documentElement.style.setProperty('--menu-h', `${menu.getBoundingClientRect().height}px`);
  }).observe(menu);
}

// Start

layout();
buildVarRows();
// Read the address before anything rewrites it.
const params = new URL(location.href).searchParams;
const startHash = location.hash;
const bulletmlLoading = loadBulletmlIndex();
const serveAt = serveBase(params);
const localLoading = serveAt ? startServe(serveAt) : Promise.resolve(null);

// A share link opens its own scene; query parameters written since (by reloading after changing
// something) win over it.
let shared: { share: Share; fragment: string } | null = null;
if (isShareHash(startHash)) {
  const fragment = startHash.slice(1);
  try {
    shared = { share: await readShare(startHash), fragment };
    applyShareScene(shared.share);
  } catch (err) {
    log.print('error', `couldn't open the shared link: ${err instanceof Error ? err.message : String(err)}`);
  }
}
for (const name of VAR_NAMES) {
  const value = params.get(name);
  if (value !== null && value.trim() !== '' && Number.isFinite(Number(value))) setVar(name, Number(value));
}
const startDifficulty = DIFFICULTIES.indexOf(params.get('difficulty') ?? '');
if (startDifficulty >= 0) setDifficulty(startDifficulty);
if (params.has('enemy') || !shared) selectEnemy(params.get('enemy') ?? 'boss');
if (params.has('move') || !shared) selectMove(params.get('move') ?? 'static');
loadDodgeSettings();
setHitboxes(startToggle('hitboxes', HITBOX_KEY));
setHeat(startToggle('heat', HEAT_KEY));
await bulletmlLoading;
const firstLocal = await localLoading;

urlReady = true;
if (shared) {
  openShared(shared.share, shared.fragment);
} else if (entries.size > 0) {
  // Older #name links still work.
  const legacy = isShareHash(startHash) ? null : startHash.slice(1) || null;
  await selectExample(params.get('example') ?? firstLocal ?? legacy);
} else {
  log.print('note', 'no examples were bundled; write a pattern and press Ctrl+Enter');
}
showCode(savedCodeShown());
// Give the sprites a moment so the first frames don't flash circles, but don't wait on a slow
// connection: they pop in as they arrive.
await Promise.race([spritesLoading, new Promise((r) => setTimeout(r, 1000))]);
lastFrame = performance.now();
requestAnimationFrame(frame);
